/**
 * TagManageRow View Component
 *
 * Renders a tag row in manage mode with edit/delete action buttons.
 * Shows the tag color, name, player usage count, and action icons.
 * @module features/player-kanban/components/TagManageRow
 */

import { Edit2, Palette, Trash2 } from "lucide-react-native";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/*
 * design-system: dynamic-style waiver — data-driven tag colour swatch (no
 * semantic token); passed as a single style reference so no inline object
 * literal renders on a JSX element.
 */
const swatchStyle = (color: string) => ({ backgroundColor: color });

/**
 * Props for TagManageRowView component
 */
interface TagManageRowViewProps {
  readonly tagName: string;
  readonly tagColor: string;
  readonly playerCount: number;
  readonly onColorEdit: () => void;
  readonly onNameEdit: () => void;
  readonly onDelete: () => void;
}

/**
 * Tag row display in manage mode.
 *
 * SE-6554: the three action glyphs used to be raw Lucide elements coloured by
 * `color=` props threaded down from the kanban palette — which is why each one
 * needed a type suppression, and why the delete glyph rendered a fixed red
 * rather than the status token. They are `Icon` atoms on semantic tokens now:
 * muted for the two edit affordances, `status-error` for the destructive one.
 *
 * The `N players` usage count stays — the manage view is specified to show it.
 * @param root0 - Component props
 * @param root0.tagName - The name of the tag
 * @param root0.tagColor - The color of the tag
 * @param root0.playerCount - Number of players with this tag
 * @param root0.onColorEdit - Callback when color edit button is pressed
 * @param root0.onNameEdit - Callback when name edit button is pressed
 * @param root0.onDelete - Callback when delete button is pressed
 */
const TagManageRowView = ({
  tagName,
  tagColor,
  playerCount,
  onColorEdit,
  onNameEdit,
  onDelete,
}: TagManageRowViewProps) => (
  <Box className="p-3">
    <Stack direction="horizontal" className="items-center justify-between">
      <Stack direction="horizontal" className="flex-1 items-center gap-3">
        {/* design-system: dynamic-style waiver — data-driven value (tag color) */}
        <Box
          className="size-4 rounded-full"
          // eslint-disable-next-line no-restricted-syntax -- data-driven tag swatch colour from arbitrary tagColor; no semantic colour token maps an arbitrary hex
          UNSAFE_style={swatchStyle(tagColor)}
        />
        <Text variant="title-sm-medium" className="flex-1 text-content-primary">
          {tagName}
        </Text>
        <Text variant="caption" className="text-content-muted">
          {playerCount} players
        </Text>
      </Stack>
      <Stack direction="horizontal" className="items-center gap-1">
        <Pressable
          onPress={onColorEdit}
          className="rounded-sm p-1"
          accessible
          accessibilityLabel={`Change color of ${tagName} tag`}
          accessibilityRole="button"
        >
          <Icon as={Palette} size="xs" className="text-content-muted" />
        </Pressable>
        <Pressable
          onPress={onNameEdit}
          className="rounded-sm p-1"
          accessible
          accessibilityLabel={`Edit ${tagName} tag`}
          accessibilityRole="button"
        >
          <Icon as={Edit2} size="xs" className="text-content-muted" />
        </Pressable>
        <Pressable
          onPress={onDelete}
          className="rounded-sm p-1"
          accessible
          accessibilityLabel={`Delete ${tagName} tag`}
          accessibilityRole="button"
        >
          <Icon as={Trash2} size="xs" className="text-status-error" />
        </Pressable>
      </Stack>
    </Stack>
  </Box>
);

TagManageRowView.displayName = "TagManageRowView";

export default memo(TagManageRowView);

export type { TagManageRowViewProps };
